import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { devicesApi } from '../api/client';

export function useDevices() {
  return useQuery({
    queryKey: ['devices'],
    queryFn: async () => {
      const response = await devicesApi.list();
      return response.data;
    },
  });
}

export function useMyDevices() {
  return useQuery({
    queryKey: ['myDevices'],
    queryFn: async () => {
      const response = await devicesApi.getMyDevices();
      return response.data;
    },
  });
}

export function useDevice(id: string) {
  return useQuery({
    queryKey: ['device', id],
    queryFn: async () => {
      const response = await devicesApi.getById(id);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useActivationCodes() {
  return useQuery({
    queryKey: ['activationCodes'],
    queryFn: async () => {
      const response = await devicesApi.getCodes();
      return response.data;
    },
  });
}

export function useCreateActivationCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data?: { assignedUserId?: string; expiresInHours?: number }) =>
      devicesApi.createCode(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activationCodes'] });
    },
  });
}

export function useUpdateDevice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; isActive?: boolean } }) =>
      devicesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
    },
  });
}

export function useDeleteDevice() {
  const queryClient = useQueryClient();

  return useMutation({
    // force=true skips the remote-uninstall wait and deletes immediately —
    // for a device that will never check in again (lost, wiped, stolen).
    // Default (false) queues a remote uninstall first if the device is
    // active; the row disappears once the agent confirms it's gone.
    mutationFn: ({ id, force = false }: { id: string; force?: boolean }) =>
      devicesApi.delete(id, force),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
    },
  });
}

export function useInstallScript(platform: string = 'win32', enabled = false) {
  return useQuery({
    queryKey: ['installScript', platform],
    queryFn: async () => {
      const response = await devicesApi.getInstallScript(platform);
      return response.data;
    },
    enabled,
    staleTime: 0, // never reuse a stale single-use activation code
  });
}

export function useResetDevice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => devicesApi.reset(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
    },
  });
}
